import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";

// Auth Pages
import OrganizerLogin from "./pages/OrganizerLogin";
import AttendeeLogin from "./pages/AttendeeLogin";
import AdminLogin from "./pages/AdminLogin";
import Landing from "./pages/Landing";

// Admin Portal Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrganizers from "./pages/admin/AdminOrganizers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminLicenses from "./pages/admin/AdminLicenses";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminProfile from "./pages/admin/AdminProfile";

// Organizer Portal Pages
import OrganizerDashboard from "./pages/organizer/OrganizerDashboard";
import OrganizerEvents from "./pages/organizer/OrganizerEvents";
import OrganizerSupport from "./pages/organizer/OrganizerSupport";
import OrganizerCart from "./pages/organizer/OrganizerCart";
import PaymentSuccess from "./pages/organizer/PaymentSuccess";
import OrganizerHelp from "./pages/organizer/Help";
import SubscriptionPlans from "./pages/organizer/SubscriptionPlans";

// Client Portal Pages
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientPayments from "./pages/client/ClientPayments";
import ClientTasks from "./pages/client/ClientTasks";
import ClientCheckin from "./pages/client/ClientCheckin";
import ClientProfile from "./pages/client/ClientProfile";

// Shared Pages (will wrap with appropriate layout)
import Members from "./pages/Members";
import Committees from "./pages/Committees";
import Announcements from "./pages/Announcements";
import Documents from "./pages/Documents";
import Payments from "./pages/Payments";
import Events from "./pages/Events";
import Profile from "./pages/Profile";
import Tasks from "./pages/Tasks";
import Checkin from "./pages/Checkin";
import Reports from "./pages/Reports";
import PricingPage from "./pages/PricingPage";

// Messaging Pages
import OrganizerMessages from "./pages/organizer/OrganizerMessages";
import AttendeeMessages from "./pages/attendee/AttendeeMessages";

// Gallery Pages
import OrganizerGallery from "./pages/organizer/OrganizerGallery";
import AttendeeGallery from "./pages/attendee/AttendeeGallery";

// Admin Email Templates
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";

// Organizer Scheduled Reports
import OrganizerScheduledReports from "./pages/organizer/OrganizerScheduledReports";

// Legal Pages
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiesPolicy from "./pages/CookiesPolicy";

// Password Reset
import ResetPassword from "./pages/ResetPassword";

// Google Auth
import GoogleAuthCallback from "./pages/GoogleAuthCallback";

// Layouts
import OrganizerLayout from "./components/layouts/OrganizerLayout";
import ClientLayout from "./components/layouts/ClientLayout";

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen bg-cream flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-maroon flex items-center justify-center mx-auto mb-4 animate-pulse">
        <span className="text-white font-heading font-bold text-2xl">b8</span>
      </div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate portal based on role
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'organizer') {
      return <Navigate to="/organizer" replace />;
    } else if (user.role === 'attendee') {
      return <Navigate to="/attendee" replace />;
    }
  }
  
  return children;
};

// Public Route Component - redirects logged-in users
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (user) {
    // Redirect to appropriate portal based on role
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'organizer') {
      return <Navigate to="/organizer" replace />;
    } else if (user.role === 'attendee') {
      return <Navigate to="/attendee" replace />;
    }
  }
  
  return children;
};

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'organizer') {
    return <Navigate to="/organizer" replace />;
  } else if (user.role === 'attendee') {
    return <Navigate to="/attendee" replace />;
  }
  
  return <Navigate to="/" replace />;
};

// Wrap shared pages with Organizer Layout
const OrganizerAttendees = () => (
  <OrganizerLayout><Members /></OrganizerLayout>
);

const OrganizerCommittees = () => (
  <OrganizerLayout><Committees /></OrganizerLayout>
);

const OrganizerAnnouncements = () => (
  <OrganizerLayout><Announcements /></OrganizerLayout>
);

const OrganizerDocuments = () => (
  <OrganizerLayout><Documents /></OrganizerLayout>
);

const OrganizerPayments = () => (
  <OrganizerLayout><Payments /></OrganizerLayout>
);

const OrganizerProgram = () => (
  <OrganizerLayout><Events /></OrganizerLayout>
);

const OrganizerProfile = () => (
  <OrganizerLayout><Profile /></OrganizerLayout>
);

const OrganizerTasks = () => (
  <OrganizerLayout><Tasks /></OrganizerLayout>
);

const OrganizerCheckin = () => (
  <OrganizerLayout><Checkin /></OrganizerLayout>
);

// Wrap shared pages with Client Layout
const ClientAnnouncements = () => (
  <ClientLayout><Announcements /></ClientLayout>
);

const ClientProgram = () => (
  <ClientLayout><Events /></ClientLayout>
);

const ClientBatchmates = () => (
  <ClientLayout><Members /></ClientLayout>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<GoogleAuthCallback />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      
      {/* Separate Login Routes */}
      <Route path="/organizer/login" element={<PublicRoute><OrganizerLogin /></PublicRoute>} />
      <Route path="/attendee/login" element={<PublicRoute><AttendeeLogin /></PublicRoute>} />
      <Route path="/admin/login" element={<PublicRoute><AdminLogin /></PublicRoute>} />
      
      {/* Legacy login routes redirect */}
      <Route path="/login" element={<Navigate to="/organizer/login" replace />} />
      <Route path="/register" element={<Navigate to="/organizer/login" replace />} />
      
      {/* Admin Portal Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/organizers" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminOrganizers />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-templates" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminEmailTemplates />
        </ProtectedRoute>
      } />
      <Route path="/admin/licenses" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLicenses />
        </ProtectedRoute>
      } />
      <Route path="/admin/support" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSupport />
        </ProtectedRoute>
      } />
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSubscriptions />
        </ProtectedRoute>
      } />
      <Route path="/admin/profile" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminProfile />
        </ProtectedRoute>
      } />
      
      {/* Pricing Page */}
      <Route path="/pricing" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <PricingPage />
        </ProtectedRoute>
      } />
      
      {/* Organizer Portal Routes */}
      <Route path="/organizer" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/organizer/events" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerEvents />
        </ProtectedRoute>
      } />
      <Route path="/organizer/attendees" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerAttendees />
        </ProtectedRoute>
      } />
      <Route path="/organizer/committees" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerCommittees />
        </ProtectedRoute>
      } />
      <Route path="/organizer/announcements" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/organizer/documents" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerDocuments />
        </ProtectedRoute>
      } />
      <Route path="/organizer/payments" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerPayments />
        </ProtectedRoute>
      } />
      <Route path="/organizer/program" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerProgram />
        </ProtectedRoute>
      } />
      <Route path="/organizer/profile" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerProfile />
        </ProtectedRoute>
      } />
      <Route path="/organizer/tasks" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerTasks />
        </ProtectedRoute>
      } />
      <Route path="/organizer/checkin" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerCheckin />
        </ProtectedRoute>
      } />
      <Route path="/organizer/reports" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <Reports />
        </ProtectedRoute>
      } />
      <Route path="/organizer/support" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerSupport />
        </ProtectedRoute>
      } />
      <Route path="/organizer/cart" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerCart />
        </ProtectedRoute>
      } />
      <Route path="/organizer/payment-success" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      <Route path="/organizer/help" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerHelp />
        </ProtectedRoute>
      } />
      <Route path="/organizer/subscription" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <SubscriptionPlans />
        </ProtectedRoute>
      } />
      <Route path="/organizer/messages" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerMessages />
        </ProtectedRoute>
      } />
      <Route path="/organizer/gallery" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerGallery />
        </ProtectedRoute>
      } />
      <Route path="/organizer/scheduled-reports" element={
        <ProtectedRoute allowedRoles={['organizer']}>
          <OrganizerScheduledReports />
        </ProtectedRoute>
      } />
      
      {/* Client/Attendee Portal Routes */}
      <Route path="/attendee" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/attendee/announcements" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/attendee/program" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientProgram />
        </ProtectedRoute>
      } />
      <Route path="/attendee/payments" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientPayments />
        </ProtectedRoute>
      } />
      <Route path="/attendee/batchmates" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientBatchmates />
        </ProtectedRoute>
      } />
      <Route path="/attendee/profile" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientProfile />
        </ProtectedRoute>
      } />
      <Route path="/attendee/tasks" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientTasks />
        </ProtectedRoute>
      } />
      <Route path="/attendee/checkin" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <ClientCheckin />
        </ProtectedRoute>
      } />
      <Route path="/attendee/messages" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <AttendeeMessages />
        </ProtectedRoute>
      } />
      <Route path="/attendee/gallery" element={
        <ProtectedRoute allowedRoles={['attendee']}>
          <AttendeeGallery />
        </ProtectedRoute>
      } />
      
      {/* Legacy routes redirect */}
      <Route path="/dashboard" element={<RoleBasedRedirect />} />
      
      {/* Default Route */}
      <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <WebSocketProvider>
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                style: {
                  fontFamily: 'Manrope, sans-serif',
                },
              }}
            />
            <AppRoutes />
          </WebSocketProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
